import { useState } from "react";
import { Icon, Avatar, Modal, Field, Inp, Sel, Txt, SaveBtn, inputStyle } from "../shared/ui.jsx";
import { USER_COLORS, USERS } from "../shared/constants.js";
import { today, displayDate } from "../shared/utils";

const PROJECTS = ["Litvill Lessons", "Litvill Lab", "Both"];
const EVENT_TYPES = ["University talk", "Conference", "Meeting", "Workshop", "Partner visit", "Press / Media", "Internal", "Other"];
const OUTCOMES = ["Great", "Good", "Neutral", "Follow-up needed", "Cancelled"];
const OUTCOME_COLORS = {
  "Great": "#4a8a6a",
  "Good": "#5b8a6b",
  "Neutral": "#8a7a60",
  "Follow-up needed": "#3d6b8a",
  "Cancelled": "#b05050",
};

export function EventForm({ onSave, onClose, existing, user }) {
  const [form, setForm] = useState(existing || {
    title: "", type: "University talk", project: "Litvill Lessons",
    date: today(), location: "", country: "", city: "",
    attendees: [], outcome: "Good",
    summary: "", nextSteps: "", photoUrl: "", docUrl: "", linkedinUrl: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleAttendee = (name) => {
    const current = form.attendees || [];
    set("attendees", current.includes(name) ? current.filter(a => a !== name) : [...current, name]);
  };

  const save = () => {
    if (!form.title.trim()) return;
    onSave({
      ...form,
      id: existing?.id || Date.now(),
      createdAt: existing?.createdAt || new Date().toISOString(),
      updatedBy: user,
    });
    onClose();
  };

  return (
    <>
      <Field label="Title *"><Inp value={form.title} onChange={e => set("title", e.target.value)} placeholder="Talk at Presidency University, Meeting with RM managers…" /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Field label="Type"><Sel value={form.type} onChange={e => set("type", e.target.value)}>{EVENT_TYPES.map(t => <option key={t}>{t}</option>)}</Sel></Field>
        <Field label="Project"><Sel value={form.project} onChange={e => set("project", e.target.value)}>{PROJECTS.map(p => <option key={p}>{p}</option>)}</Sel></Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Field label="Date"><Inp type="date" value={form.date} onChange={e => set("date", e.target.value)} /></Field>
        <Field label="Outcome">
          <Sel value={form.outcome} onChange={e => set("outcome", e.target.value)}>
            {OUTCOMES.map(o => <option key={o}>{o}</option>)}
          </Sel>
        </Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Field label="City"><Inp value={form.city} onChange={e => set("city", e.target.value)} placeholder="Bangalore" /></Field>
        <Field label="Country"><Inp value={form.country} onChange={e => set("country", e.target.value)} placeholder="India" /></Field>
      </div>
      <Field label="Venue / Location"><Inp value={form.location} onChange={e => set("location", e.target.value)} placeholder="Presidency University, main auditorium" /></Field>

      <Field label="Who attended">
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {USERS.map(u => (
            <button key={u} type="button" onClick={() => toggleAttendee(u)} style={{
              padding: "0.35rem 0.75rem", borderRadius: 3, cursor: "pointer", fontSize: "0.82rem",
              border: `2px solid ${(form.attendees || []).includes(u) ? USER_COLORS[u] : "var(--ink20)"}`,
              background: (form.attendees || []).includes(u) ? USER_COLORS[u] + "22" : "transparent",
              color: (form.attendees || []).includes(u) ? USER_COLORS[u] : "var(--ink50)",
              fontWeight: (form.attendees || []).includes(u) ? 600 : 400,
            }}>{u}</button>
          ))}
        </div>
      </Field>

      <Field label="Summary"><Txt value={form.summary} onChange={e => set("summary", e.target.value)} placeholder="What happened, who we met, what was discussed…" /></Field>
      <Field label="Next steps"><Txt value={form.nextSteps} onChange={e => set("nextSteps", e.target.value)} placeholder="Follow up with Dr. Sharma, send MoU draft…" style={{ minHeight: 56 }} /></Field>

      <div style={{ borderTop: "1px solid var(--ink10)", margin: "1rem 0 0.75rem", paddingTop: "0.75rem", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--ink50)" }}>Links (optional)</div>
      <Field label="Photos"><Inp value={form.photoUrl} onChange={e => set("photoUrl", e.target.value)} placeholder="Google Photos album URL…" /></Field>
      <Field label="Document / Slides"><Inp value={form.docUrl} onChange={e => set("docUrl", e.target.value)} placeholder="Google Drive link…" /></Field>
      <Field label="LinkedIn post"><Inp value={form.linkedinUrl} onChange={e => set("linkedinUrl", e.target.value)} placeholder="https://linkedin.com/posts/…" /></Field>
      <SaveBtn onClick={save} />
    </>
  );
}

function EventCard({ event, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const projectColor = event.project === "Litvill Lessons" ? "#3d6b8a" : event.project === "Litvill Lab" ? "#5b8a6b" : "#7a5c2e";
  const outcomeColor = OUTCOME_COLORS[event.outcome] || "var(--ink50)";
  const hasLinks = event.photoUrl || event.docUrl || event.linkedinUrl;

  return (
    <div style={{ background: "var(--paper-dark)", borderRadius: 4, marginBottom: "0.6rem", border: "1px solid var(--ink10)", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "0.85rem 1rem", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }} onClick={() => setExpanded(v => !v)}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.08em", color: projectColor, fontWeight: 600 }}>{event.project}</span>
            <span style={{ fontSize: "0.65rem", color: "var(--ink30)" }}>· {event.type}</span>
            <span style={{ fontSize: "0.65rem", color: "var(--ink30)" }}>· {displayDate(event.date)}</span>
          </div>
          <div style={{ fontFamily: "var(--font-head)", fontSize: "1rem", color: "var(--ink)" }}>{event.title}</div>
          <div style={{ fontSize: "0.78rem", color: "var(--ink50)", marginTop: 2 }}>
            {[event.city, event.country].filter(Boolean).join(", ")}
            {event.location ? ` · ${event.location}` : ""}
          </div>
          {(event.attendees || []).length > 0 && (
            <div style={{ display: "flex", gap: 4, marginTop: 5 }}>
              {event.attendees.map(a => <Avatar key={a} user={a} size={18} />)}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0, marginLeft: "0.5rem" }}>
          <span style={{ fontSize: "0.7rem", padding: "2px 8px", borderRadius: 10, background: outcomeColor + "22", color: outcomeColor, fontWeight: 600, whiteSpace: "nowrap" }}>{event.outcome}</span>
          <Icon name={expanded ? "chevronUp" : "chevron"} size={14} />
        </div>
      </div>

      {/* Expanded */}
      {expanded && (
        <div style={{ borderTop: "1px solid var(--ink10)", padding: "0.85rem 1rem" }}>
          {event.summary && (
            <div style={{ marginBottom: "0.75rem" }}>
              <div style={{ fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--ink30)", marginBottom: 4 }}>Summary</div>
              <div style={{ fontSize: "0.85rem", color: "var(--ink70)" }}>{event.summary}</div>
            </div>
          )}
          {event.nextSteps && (
            <div style={{ marginBottom: "0.75rem" }}>
              <div style={{ fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--ink30)", marginBottom: 4 }}>Next steps</div>
              <div style={{ fontSize: "0.85rem", color: "var(--ink70)" }}>{event.nextSteps}</div>
            </div>
          )}
          {hasLinks && (
            <div style={{ marginBottom: "0.75rem", display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              {event.photoUrl && <a href={event.photoUrl} target="_blank" rel="noreferrer" style={{ fontSize: "0.8rem", color: "var(--accent)", textDecoration: "none" }}>📷 Photos ↗</a>}
              {event.docUrl && <a href={event.docUrl} target="_blank" rel="noreferrer" style={{ fontSize: "0.8rem", color: "var(--accent)", textDecoration: "none" }}>📄 Document ↗</a>}
              {event.linkedinUrl && <a href={event.linkedinUrl} target="_blank" rel="noreferrer" style={{ fontSize: "0.8rem", color: "var(--accent)", textDecoration: "none" }}>🔗 LinkedIn ↗</a>}
            </div>
          )}
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button onClick={() => onEdit(event)} style={{ fontSize: "0.78rem", padding: "0.3rem 0.7rem", border: "1px solid var(--ink20)", borderRadius: 3, cursor: "pointer", background: "transparent", color: "var(--ink50)" }}>Edit</button>
            <button onClick={() => onDelete(event.id)} style={{ fontSize: "0.78rem", padding: "0.3rem 0.7rem", border: "1px solid var(--ink20)", borderRadius: 3, cursor: "pointer", background: "transparent", color: "var(--ink30)" }}>Delete</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function EventsTab({ user, events, setEvents, searchQ }) {
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filterProject, setFilterProject] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterAttendee, setFilterAttendee] = useState("all");

  let items = [...events].sort((a, b) => b.date.localeCompare(a.date));
  if (searchQ) items = items.filter(e =>
    e.title.toLowerCase().includes(searchQ) ||
    (e.summary || "").toLowerCase().includes(searchQ) ||
    (e.nextSteps || "").toLowerCase().includes(searchQ) ||
    (e.location || "").toLowerCase().includes(searchQ) ||
    (e.city || "").toLowerCase().includes(searchQ)
  );
  if (filterProject !== "all") items = items.filter(e => e.project === filterProject);
  if (filterType !== "all") items = items.filter(e => e.type === filterType);
  if (filterAttendee !== "all") items = items.filter(e => (e.attendees || []).includes(filterAttendee));

  const save = (event) => {
    if (editItem) setEvents(events.map(e => e.id === event.id ? event : e));
    else setEvents([...events, event]);
    setEditItem(null); setShowForm(false);
  };
  const del = (id) => setEvents(events.filter(e => e.id !== id));

  return (
    <div>
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
        <Sel value={filterProject} onChange={e => setFilterProject(e.target.value)} style={{ ...inputStyle, width: "auto", fontSize: "0.8rem" }}>
          <option value="all">All projects</option>
          {PROJECTS.map(p => <option key={p}>{p}</option>)}
        </Sel>
        <Sel value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...inputStyle, width: "auto", fontSize: "0.8rem" }}>
          <option value="all">All types</option>
          {EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
        </Sel>
        <Sel value={filterAttendee} onChange={e => setFilterAttendee(e.target.value)} style={{ ...inputStyle, width: "auto", fontSize: "0.8rem" }}>
          <option value="all">All attendees</option>
          {USERS.map(u => <option key={u}>{u}</option>)}
        </Sel>
      </div>

      <div style={{ fontSize: "0.78rem", color: "var(--ink50)", marginBottom: "0.5rem" }}>{items.length} event(s) · shared</div>
      {items.length === 0 && <p style={{ color: "var(--ink30)", fontStyle: "italic", fontSize: "0.9rem" }}>No events found.</p>}
      {items.map(e => (
        <EventCard key={e.id} event={e} onEdit={item => { setEditItem(item); setShowForm(true); }} onDelete={del} />
      ))}

      {showForm && (
        <Modal title={editItem ? "Edit Event" : "New Event"} onClose={() => { setShowForm(false); setEditItem(null); }}>
          <EventForm user={user} onSave={save} onClose={() => { setShowForm(false); setEditItem(null); }} existing={editItem} />
        </Modal>
      )}
    </div>
  );
}